import React, { useEffect, useMemo, useState } from 'react';
import {
  Box, Typography, Chip, Button, TextField,
  Table, TableHead, TableBody, TableRow, TableCell, TableContainer,
  CircularProgress, Select, MenuItem, FormControl, InputLabel,
  Dialog, DialogTitle, DialogContent, DialogActions, Alert,
} from '@mui/material';
import HourglassBottomIcon from '@mui/icons-material/HourglassBottom';
import { getAppointments, getWaitlist, createWaitlistEntry, deleteWaitlistEntry } from '../../services/api';
import { useAuth } from '../../hooks/useAuth';
import { useClinicFeatures } from '../../hooks/useClinicFeatures';
import { useNotification } from '../../hooks/useNotification';
import { APPOINTMENT_STATE_CONFIG as STATE_CONFIG } from '../../constants/appointmentStates';
import AppointmentDialog from '../AppointmentDialog';
import { useNewAppointmentFlow } from '../NewAppointmentFlow';

// ── Turnos del paciente ───────────────────────────────────────────────────────
export const AppointmentsTab = ({ patient, doctors: doctorsArr = [] }) => {
  const { isAdmin, isRecepcionista, isMedico, doctorId: myDoctorId } = useAuth();
  const { features } = useClinicFeatures();
  const notify = useNotification();
  // El médico también puede agregar a la lista de espera de su propia
  // agenda (pedido explícito) — el selector de médico más abajo se limita
  // a su propio doctorId en ese caso, porque el backend rechaza agregar a
  // la lista de espera de otro médico.
  const canManageWaitlist = (isAdmin || isRecepcionista || isMedico) && features?.waitlist;
  const waitlistDoctorOptions = isMedico && !isAdmin && !isRecepcionista
    ? doctorsArr.filter(d => d.id === myDoctorId)
    : doctorsArr;
  const [appointments, setAppointments] = useState([]);
  const [loading, setLoading]           = useState(true);
  const [waitlistEntries, setWaitlistEntries] = useState([]);
  const [waitlistDialogOpen, setWaitlistDialogOpen] = useState(false);
  const [waitlistDoctorId, setWaitlistDoctorId] = useState('');
  const [waitlistNotes, setWaitlistNotes] = useState('');
  const [savingWaitlist, setSavingWaitlist] = useState(false);
  // Pedido explícito: poder abrir/editar un turno haciendo click en la
  // fila, en vez de tener que ir a buscarlo a la Agenda o a /turnos — la
  // tabla ya se mostraba con hover como si fuera clickeable, pero no hacía
  // nada. Mismo patrón que AppointmentSearch.js: un solo AppointmentDialog
  // montado acá, alimentado con el turno clickeado o con el turno nuevo
  // armado por useNewAppointmentFlow (mismo dialogOpen/dialogEvent para
  // ambos casos — isEdit se decide solo por si el evento ya tiene id).
  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogEvent, setDialogEvent] = useState(null);

  const reloadAppointments = () => {
    getAppointments({ patient_id: patient.id, limit: 200 })
      .then(res => setAppointments(res.data?.items || []))
      .catch(() => {});
  };

  const handleRowClick = (ap) => {
    setDialogEvent(ap);
    setDialogOpen(true);
  };

  const {
    canCreateAppointment, showForm: showNewAppointmentForm,
    closeForm: closeNewAppointmentForm, handleContinue: handleStartNewAppointment,
    canContinue: canContinueNewAppointment, FormFields: NewAppointmentFormFields,
    TriggerButton: NewAppointmentTriggerButton,
  } = useNewAppointmentFlow({
    patient, doctorsArr,
    onContinue: (event) => { setDialogEvent(event); setDialogOpen(true); },
  });

  const doctorsMap = useMemo(() => {
    const m = {};
    doctorsArr.forEach(d => { m[d.id] = d; });
    return m;
  }, [doctorsArr]);

  const loadWaitlist = () => {
    if (!canManageWaitlist) return;
    getWaitlist({ patient_id: patient.id, status: 'activo' })
      .then(res => setWaitlistEntries(res.data || []))
      .catch(() => setWaitlistEntries([]));
  };

  useEffect(() => {
    getAppointments({ patient_id: patient.id, limit: 200 })
      .then(res => setAppointments(res.data?.items || []))
      .catch(() => {})
      .finally(() => setLoading(false));
    loadWaitlist();
    // eslint-disable-next-line
  }, [patient.id]);

  const alreadyWaitingDoctorIds = useMemo(
    () => new Set(waitlistEntries.map(w => w.doctor_id)),
    [waitlistEntries]
  );
  const alreadyWaitingForSelected = waitlistDoctorId && alreadyWaitingDoctorIds.has(Number(waitlistDoctorId));

  const handleAddToWaitlist = async () => {
    if (!waitlistDoctorId) return;
    setSavingWaitlist(true);
    try {
      await createWaitlistEntry({ doctor_id: Number(waitlistDoctorId), patient_id: patient.id, notes: waitlistNotes || null });
      notify('Paciente agregado a la lista de espera', 'success');
      setWaitlistDialogOpen(false);
      setWaitlistDoctorId('');
      setWaitlistNotes('');
      loadWaitlist();
    } catch (err) {
      notify(err.userMessage || 'Error al agregar a la lista de espera', 'error');
    } finally {
      setSavingWaitlist(false);
    }
  };

  const handleRemoveFromWaitlist = async (entry) => {
    if (!window.confirm(`¿Sacar a ${patient.name} de la lista de espera de ${entry.doctor?.name || 'este médico'}?`)) return;
    try {
      await deleteWaitlistEntry(entry.id);
      notify('Sacado de la lista de espera', 'success');
      loadWaitlist();
    } catch (err) {
      notify(err.userMessage || 'Error al quitar de la lista de espera', 'error');
    }
  };

  return (
    <Box>
      {canManageWaitlist && waitlistEntries.length > 0 && (
        <Box sx={{ mb: 1.5, p: 1.5, borderRadius: 1, bgcolor: 'warning.light', border: '1px solid', borderColor: 'warning.main' }}>
          <Typography variant="caption" fontWeight={700} color="warning.dark" sx={{ display: 'block', mb: 0.5 }}>
            En lista de espera
          </Typography>
          {waitlistEntries.map(entry => (
            <Box key={entry.id} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1, py: 0.25 }}>
              <Typography variant="body2">
                {entry.doctor?.name || 'Médico'}
                {entry.notes && <Typography component="span" variant="caption" color="text.secondary"> — {entry.notes}</Typography>}
              </Typography>
              <Button size="small" color="inherit" onClick={() => handleRemoveFromWaitlist(entry)} sx={{ minWidth: 0, textTransform: 'none' }}>
                Quitar
              </Button>
            </Box>
          ))}
        </Box>
      )}
      {(canCreateAppointment || canManageWaitlist) && (
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, mb: 1.5, flexWrap: 'wrap' }}>
          {canCreateAppointment && !showNewAppointmentForm && <NewAppointmentTriggerButton />}
          {canManageWaitlist && (
            <Button size="small" variant="outlined" startIcon={<HourglassBottomIcon fontSize="small" />} onClick={() => {
              if (isMedico && !isAdmin && !isRecepcionista && myDoctorId) setWaitlistDoctorId(myDoctorId);
              setWaitlistDialogOpen(true);
            }}>
              Agregar a lista de espera
            </Button>
          )}
        </Box>
      )}
      {showNewAppointmentForm && (
        <Box sx={{ display: 'flex', alignItems: 'flex-end', gap: 1.5, mb: 1.5, p: 1.5, borderRadius: 1, bgcolor: 'primary.50', border: '1px solid', borderColor: 'primary.light', flexWrap: 'wrap' }}>
          <NewAppointmentFormFields />
          <Button size="small" onClick={closeNewAppointmentForm}>Cancelar</Button>
          <Button size="small" variant="contained" disabled={!canContinueNewAppointment} onClick={handleStartNewAppointment}>
            Continuar
          </Button>
        </Box>
      )}
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}><CircularProgress size={24} /></Box>
      ) : appointments.length === 0 ? (
        <Typography color="text.secondary" variant="body2" sx={{ py: 3, textAlign: 'center' }}>
          Sin turnos registrados.
        </Typography>
      ) : (
        <TableContainer sx={{ overflowX: 'auto' }}>
          <Table size="small" sx={{ minWidth: 420 }}>
            <TableHead>
              <TableRow sx={{ '& th': { fontWeight: 700, fontSize: '0.72rem', py: 0.75, px: 1, whiteSpace: 'nowrap' } }}>
                <TableCell>Fecha</TableCell>
                <TableCell>Doctor</TableCell>
                <TableCell>Estado</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {appointments.map(ap => {
                const doc   = doctorsMap[ap.doctor_id];
                const dt    = new Date(ap.start_time + 'Z');
                const state = STATE_CONFIG[ap.state] || STATE_CONFIG.pendiente;
                return (
                  <TableRow key={ap.id} hover onClick={() => handleRowClick(ap)} sx={{ '& td': { py: 0.5, px: 1 }, cursor: 'pointer' }}>
                    <TableCell sx={{ whiteSpace: 'nowrap' }}>
                      <Typography variant="body2" fontWeight={600} sx={{ lineHeight: 1.2 }}>
                        {dt.toLocaleDateString('es-AR', { day: '2-digit', month: '2-digit', year: '2-digit' })}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {dt.toLocaleTimeString('es-AR', { hour: '2-digit', minute: '2-digit' })}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" noWrap sx={{ maxWidth: 120 }}>
                        {doc ? doc.name : <Typography component="span" variant="caption" color="text.disabled">—</Typography>}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Chip label={state.label} size="small"
                        sx={{ bgcolor: state.color, color: 'white', fontWeight: 600, fontSize: '0.65rem', height: 20 }} />
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      <Dialog open={waitlistDialogOpen} onClose={() => setWaitlistDialogOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Agregar a lista de espera</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Si se libera un turno de este médico, va a aparecer acá para contactar a {patient.name} manualmente.
          </Typography>
          <FormControl fullWidth size="small" sx={{ mb: 2 }}>
            <InputLabel>Médico *</InputLabel>
            <Select value={waitlistDoctorId} label="Médico *" onChange={e => setWaitlistDoctorId(e.target.value)}>
              {waitlistDoctorOptions.map(d => <MenuItem key={d.id} value={d.id}>{d.name}{d.specialty ? ` — ${d.specialty}` : ''}</MenuItem>)}
            </Select>
          </FormControl>
          {alreadyWaitingForSelected && (
            <Alert severity="warning" sx={{ mb: 2 }}>
              {patient.name} ya está en la lista de espera de este médico.
            </Alert>
          )}
          <TextField
            label="Notas" fullWidth multiline rows={2} size="small"
            value={waitlistNotes} onChange={e => setWaitlistNotes(e.target.value)}
            placeholder="Ej: prefiere turnos por la mañana"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setWaitlistDialogOpen(false)}>Cancelar</Button>
          <Button
            variant="contained"
            disabled={!waitlistDoctorId || savingWaitlist || alreadyWaitingForSelected}
            onClick={handleAddToWaitlist}
          >
            {savingWaitlist ? <CircularProgress size={18} /> : 'Agregar'}
          </Button>
        </DialogActions>
      </Dialog>

      {dialogOpen && (
        <AppointmentDialog
          open={dialogOpen}
          onClose={() => setDialogOpen(false)}
          isEdit={!!dialogEvent?.id}
          selectedEvent={dialogEvent}
          doctorId={dialogEvent?.doctor_id}
          doctor={doctorsMap[dialogEvent?.doctor_id]}
          patients={[patient]}
          setPatients={() => {}}
          onSaved={() => { setDialogOpen(false); reloadAppointments(); }}
        />
      )}
    </Box>
  );
};
